/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { formatDate as formatDateUtc } from '../../utils/dateTime';

const ReferenceTooltip = ({ assignment, workflow, show, onClose, theme, triggerRef, type = 'reference', className = '' }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, arrowLeft: null, arrowRight: null });

  const calcPos = useCallback(() => {
    if (triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      if (type === 'name') {
        setPos({
          top: rect.bottom + 10,
          arrowLeft: 20,
          arrowRight: null,
        });
      } else {
        setPos({
          top: rect.bottom + 10,
          arrowLeft: null,
          arrowRight: 0,
        });
      }
    }
  }, [triggerRef, type]);

  useEffect(() => {
    if (show) {
      calcPos();
      // Recalculate on scroll so tooltip follows the button
      window.addEventListener('scroll', calcPos, true);
      return () => window.removeEventListener('scroll', calcPos, true);
    }
  }, [show, calcPos]);

  if (!show) { return null; }

  const formatDate = (dateString) => {
    if (!dateString) { return 'March 24, 2026'; }
    try {
      return formatDateUtc(dateString);
    } catch {
      return 'March 24, 2026';
    }
  };

  const creatorName = assignment?.creatorName || '';
  const date = formatDate(assignment?.createdAt);
  const reason = workflow?.name || 'Initiated by customer';

  const arrowStyle = (zIndex, topOffset, size) => ({
    position: 'absolute',
    top: topOffset,
    left: pos.arrowLeft !== null ? `${pos.arrowLeft}px` : 'auto',
    right: pos.arrowRight !== null ? `${pos.arrowRight}px` : 'auto',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: `${size}px solid transparent`,
    borderRight: `${size}px solid transparent`,
    borderBottomWidth: `${size}px`,
    borderBottomStyle: 'solid',
    zIndex,
  });

  return createPortal(
    <>
      <div className="fixed inset-0 z-[10000]" onClick={onClose} />

      <div
        ref={containerRef}
        className={`reference-tooltip-container ${className}`.trim()}
        style={{
          position: 'fixed',
          top: `${pos.top}px`,
          left: '16px',
          right: '16px',
          zIndex: 10001,
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        <div data-theme={theme} style={{ position: 'relative' }} className='rounded-[10px]'>
          <div className="border-b-primary" style={arrowStyle(1, '-10px', 10)} />
          <div className="border-b-base-100" style={arrowStyle(2, '-7px', 10)} />

          <div className="bg-base-100 border-[2px] border-primary rounded-[10px] p-[25px_16px] shadow-[0_2px_8px_rgba(0,0,0,0.12)] w-full">
            <div className="space-y-1 text-base-content text-[20px] leading-tight custom-line-break select-none">
              {type === 'name' ? (
                <p className="font-bold">{assignment.submitterName || assignment.workflowName || 'Valued Customer'}</p>
              ) : (
                <>
                  <p><span>Sent by:</span> <span>{creatorName}</span></p>
                  <p><span>Date:</span> <span>{date}</span></p>
                  <p><span>Reason:</span> <span>{reason}</span></p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ReferenceTooltip;

ReferenceTooltip.propTypes = {
  assignment: PropTypes.shape({
    creatorName: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    name: PropTypes.string,
  }),
  show: PropTypes.bool,
  onClose: PropTypes.func,
  theme: PropTypes.string,
  triggerRef: PropTypes.shape({
    current: PropTypes.object,
  }),
  type: PropTypes.string,
};
